import React, { useCallback, useEffect, useRef, useState } from "react";
import AppSettings from "../../../config";
import { compact } from "lodash";
import ArcgisConfig from "@arcgis/core/config";
import Graphic from "@arcgis/core/Graphic";
import MapView from "@arcgis/core/views/MapView";
import Map from "@arcgis/core/Map";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Zoom from "@arcgis/core/widgets/Zoom";
import {
  getArcGISGeometryType,
  getBoundingBoxOfArcGISGeometries,
  getSymbolForGeometryType,
} from "../utils/arcgisHelper";
import { geojsonToArcGIS } from "@terraformer/arcgis";
import { searchFeatures } from "../queries/maps";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import { ADHOC_LAYER_ID } from "../hooks/selector/useAdhocFeatures";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { Loader } from "semantic-ui-react";

export interface StaticMapProps {
  apiKey?: string;
  additionalVectorLayers?: string[];
  basemap?: string;
  featureIds?: number[];
  features?: Record<string, unknown>[];
  center?: [number, number];
  zoom?: number;
  classes?: string;
  onMapViewLoaded?: (view: MapView, real: boolean) => void;
  onMapViewLoadedDelay?: number;
  expandExtent?: number;
}

/**
 * A fake static map to display what will eventually be actual static maps, immediately.
 * Note: Be very careful when updating the state logic here. Very easy to run into race
 * conditions and infinite loops. Make sure you check the geotargeting form and the map
 * screenshot page that the static map worker uses to make sure they still work, including
 * with a performance limit of 20x CPU throttle.
 */
export function StaticMap({
  apiKey = AppSettings.ARCGIS_API_KEY,
  basemap = "streets-navigation-vector",
  featureIds = [],
  features = [],
  center,
  zoom,
  additionalVectorLayers = [],
  classes = "tw-bg-grey-light tw-h-full tw-w-full tw-overflow-hidden tw-rounded-[2.7px]",
  onMapViewLoaded,
  onMapViewLoadedDelay = 100,
  expandExtent = 1.15,
}: StaticMapProps) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<MapView | null>(null);
  const staticLayerRef = useRef<GraphicsLayer | null>(null);
  const onMapViewLoadedTimeoutRef = useRef<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewInitialized, setViewInitialized] = useState(false);
  const [featuresInitialized, setFeaturesInitialized] = useState(false);
  const networkHeaders = useNetworkHeaders();

  const scheduleOnMapViewLoaded = useCallback(() => {
    if (onMapViewLoadedTimeoutRef.current && onMapViewLoaded) {
      clearTimeout(onMapViewLoadedTimeoutRef.current);
    }

    onMapViewLoadedTimeoutRef.current = setTimeout(() => {
      onMapViewLoaded?.(viewRef.current!, true);
      onMapViewLoadedTimeoutRef.current = null;
    }, onMapViewLoadedDelay);
  }, [onMapViewLoaded, onMapViewLoadedDelay]);

  useEffect(() => {
    if (!featuresInitialized || !viewInitialized) return;

    // There's some exceptionally weird state issues here.
    // For whatever reason, the graphics don't visibly show up
    // until the parent rerenders...

    onMapViewLoaded?.(viewRef.current!, !(featureIds.length || features.length));
    scheduleOnMapViewLoaded();
    reactiveUtils.watch(
      () => staticLayerRef.current?.graphics.map((g) => g.layer),
      (layers) => {
        if (layers && layers.every((layer) => !!layer)) {
          scheduleOnMapViewLoaded();
        }
      },
    );

    return () => {
      if (onMapViewLoadedTimeoutRef.current && onMapViewLoaded) {
        clearTimeout(onMapViewLoadedTimeoutRef.current);
        onMapViewLoadedTimeoutRef.current = null;
      }
    };
  }, [featuresInitialized, viewInitialized, onMapViewLoaded, onMapViewLoadedDelay]);

  const fetchFeaturesByIds = async (featureIds: number[] | { type: string; id: number; layerId?: number }[]) => {
    if (!featureIds.length) return [];

    setLoading(true);
    try {
      const idsParam = (featureIds as number[]).map((id) => id.toString());
      const response = await searchFeatures(networkHeaders)({
        id: idsParam,
        size: idsParam.length,
      });

      if (response && response.hits && response.hits.hits) {
        return response.hits.hits.map((hit) => {
          const feature = hit._source;
          const isAdhoc = feature.feature_type === "Global" && !feature.layer_id;

          return new Graphic({
            geometry: {
              ...geojsonToArcGIS(feature.geometry as GeoJSON.GeoJSON),
              type: getArcGISGeometryType(feature.geometry.type),
            },
            attributes: {
              id: feature.id,
              name: feature.primary_field_value || `Feature ${feature.id}`,
              type: isAdhoc ? ADHOC_LAYER_ID : "predefined",
              layerId: feature.layer_id,
            },
            symbol: getSymbolForGeometryType(feature.geometry.type),
          });
        });
      }
      return [];
    } catch (error) {
      console.error("Error fetching features by IDs:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const convertGeoJSONFeaturesToGraphics = (geoJsonFeatures: Record<string, unknown>[]): Graphic[] => {
    return geoJsonFeatures.flatMap((feature, index) => {
      if (feature.type === "FeatureCollection") {
        return convertGeoJSONFeaturesToGraphics(feature.features as Record<string, unknown>[]);
      }
      const geometry = (feature as any).geometry || feature;
      const properties = (feature as any).properties || {};

      return new Graphic({
        geometry: {
          ...geojsonToArcGIS(geometry as GeoJSON.GeoJSON),
          type: getArcGISGeometryType(geometry.type),
        },
        attributes: {
          id: `geojson-${index}`,
          name: properties.name || `GeoJSON Feature ${index + 1}`,
          type: "geojson",
          ...properties,
        },
        symbol: getSymbolForGeometryType(geometry.type),
      });
    });
  };

  useEffect(() => {
    if (!mapRef.current) return;
    ArcgisConfig.apiKey = apiKey;

    const staticLayer = new GraphicsLayer({
      id: "staticLayer",
      graphics: [],
    });
    staticLayerRef.current = staticLayer;

    const additionalLayers = [];
    for (const vtl of additionalVectorLayers) {
      const vtlLayer = new VectorTileLayer({
        url: vtl,
      });
      vtlLayer.opacity = 0.5;
      additionalLayers.push(vtlLayer);
    }

    const map = new Map({
      basemap,
      layers: compact([...additionalLayers, staticLayer]),
    });
    const view = new MapView({
      container: mapRef.current,
      center,
      zoom,
      map,
      navigation: {
        // Disable all navigation actions
        browserTouchPanEnabled: false,
        actionMap: {
          dragPrimary: "none",
          dragSecondary: "none",
          dragTertiary: "none",
          mouseWheel: "none",
        },
      },
      constraints: {
        // Disable snapping to the nearest zoom level so we can get a more precise extent after features are added.
        snapToZoom: false,
      },
    });
    viewRef.current = view;

    view.when(async () => {
      view.ui.move("zoom", "bottom-right");
      const zoom = view.ui.getComponents("bottom-right")[0] as Zoom;
      zoom.visible = false;

      reactiveUtils.when(
        () => !view.updating,
        () => setViewInitialized(true),
      );

      // Watch for view updating changes and restart the timer when updating starts
      reactiveUtils.watch(
        () => view.updating,
        (updating) => {
          if (updating) {
            if (onMapViewLoadedTimeoutRef.current && onMapViewLoaded) {
              clearTimeout(onMapViewLoadedTimeoutRef.current);
              onMapViewLoadedTimeoutRef.current = null;
            }
          } else {
            scheduleOnMapViewLoaded();
          }
        },
      );
    });

    return () => {
      if (onMapViewLoadedTimeoutRef.current && onMapViewLoaded) {
        clearTimeout(onMapViewLoadedTimeoutRef.current);
        onMapViewLoadedTimeoutRef.current = null;
      }
      viewRef.current?.destroy();
    };
  }, [!!mapRef.current]);

  const prevFeatureIds = useRef<number[]>([]);
  const prevFeatures = useRef<Record<string, unknown>[]>([]);

  useEffect(() => {
    (async () => {
      if (
        JSON.stringify(prevFeatureIds.current) === JSON.stringify(featureIds) &&
        JSON.stringify(prevFeatures.current) === JSON.stringify(features) &&
        (featuresInitialized || featureIds?.length)
      ) {
        return;
      }
      prevFeatureIds.current = featureIds;
      prevFeatures.current = features;
      if (!viewRef.current?.map || !staticLayerRef.current) return;
      if (!featureIds?.length && !features?.length) return setFeaturesInitialized(true);

      const allGraphics: Graphic[] = [];
      if (featureIds?.length) {
        const featureGraphics = await fetchFeaturesByIds(featureIds);
        allGraphics.push(...featureGraphics);
      }
      if (features?.length) {
        try {
          const geoJsonGraphics = convertGeoJSONFeaturesToGraphics(features);
          allGraphics.push(...geoJsonGraphics);
        } catch (error) {
          console.error("Error converting GeoJSON features:", error);
        }
      }

      if (allGraphics.length) {
        staticLayerRef.current.removeAll();
        staticLayerRef.current.addMany(allGraphics);
        const extent = await getBoundingBoxOfArcGISGeometries(compact(allGraphics.map((g) => g.geometry)));
        if (extent) {
          extent.expand(expandExtent);
          viewRef.current.extent = extent;
        }
      }

      setFeaturesInitialized(true);
    })();
  }, [featureIds, features, viewInitialized]);

  useEffect(() => {
    if (!viewRef.current || featureIds.length || features.length) return;
    if (center) viewRef.current.center = center;
    if (zoom) viewRef.current.zoom = zoom;
  }, [center, zoom]);

  return (
    <>
      <div className={classes} ref={mapRef} />
      {loading && (
        <div className='tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-white tw-bg-opacity-50'>
          <Loader active size='large'>
            Loading...
          </Loader>
        </div>
      )}
    </>
  );
}
